import React, { useEffect, useMemo, useState } from "react";
import { PurchaseOrderDetail, ReceiveRequest } from "../types";
import { purchasingService } from "../services/dataService";
import { usePermissions } from "../context/PermissionContext";
import { todayLocal } from "../utils/dates";
import "./SendDocumentModal.css";
import "../pages/Receivables.css";
import "../pages/Purchasing.css";

interface Props {
  order: PurchaseOrderDetail;
  onClose: () => void;
  onSaved: () => void;
}

const today = () => todayLocal();
const fmtQty = (n: number) => n.toLocaleString("tr-TR", { maximumFractionDigits: 3 });

/** Mal kabul: satır bazında teslim alınan miktar; kalanı aşamaz. */
const ReceiveModal: React.FC<Props> = ({ order, onClose, onSaved }) => {
  const { hasModule } = usePermissions();
  const open = useMemo(() => order.lines.filter((l) => l.remaining > 0.0005), [order.lines]);
  const [qty, setQty] = useState<Record<string, string>>(() =>
    Object.fromEntries(open.map((l) => [l.purchaseOrderLineId, String(l.remaining)])));
  const [receiptDate, setReceiptDate] = useState(today());
  const [waybillNo, setWaybillNo] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !saving) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  const parse = (s: string) => { const n = parseFloat((s || "").replace(",", ".")); return isNaN(n) ? 0 : n; };
  const totalLines = open.filter((l) => parse(qty[l.purchaseOrderLineId]) > 0).length;

  const fillAll = () => setQty(Object.fromEntries(open.map((l) => [l.purchaseOrderLineId, String(l.remaining)])));
  const clearAll = () => setQty(Object.fromEntries(open.map((l) => [l.purchaseOrderLineId, ""])));

  const save = async () => {
    const lines = open
      .map((l) => ({ purchaseOrderLineId: l.purchaseOrderLineId, quantity: parse(qty[l.purchaseOrderLineId]) }))
      .filter((l) => l.quantity > 0);
    if (lines.length === 0) return setError("En az bir satırda miktar girin.");
    for (const l of lines) {
      const src = open.find((o) => o.purchaseOrderLineId === l.purchaseOrderLineId)!;
      if (l.quantity > src.remaining + 0.0005) return setError(`${src.stockName}: kalan ${fmtQty(src.remaining)} ${src.unit} aşılamaz.`);
    }
    const dto: ReceiveRequest = { receiptDate, waybillNo: waybillNo.trim() || null, note: note.trim() || null, lines };
    setSaving(true); setError("");
    try { await purchasingService.receive(order.purchaseOrderId, dto); onSaved(); onClose(); }
    catch (err: any) { const d = err?.response?.data; setError(typeof d === "string" ? d : d?.message || "Mal kabul kaydedilemedi."); }
    finally { setSaving(false); }
  };

  return (
    <div className="ic-overlay rc-page" onClick={() => !saving && onClose()}>
      <div className="ic-panel" style={{ maxWidth: "52rem" }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <header className="ic-bar">
          <div className="ic-bar-left">
            <h2>Mal Kabul</h2>
            <span className="ic-chip neutral">{order.number}</span>
            <span className="ic-chip neutral">{order.supplierName}</span>
            {!hasModule("stock") && <span className="ic-chip proforma" title="Stok modülü lisansta yok">Stok hareketi üretilmez</span>}
          </div>
          <button className="ic-close" onClick={onClose} aria-label="Kapat" disabled={saving}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </header>

        <div className="ic-body">
          {error && <div className="ic-alert error">{error}</div>}
          <div className="ic-doc" style={{ padding: "1.25rem 1.5rem" }}>
            <div className="rc-form" style={{ gridTemplateColumns: "1fr 1fr 2fr", marginBottom: "1rem" }}>
              <div className="rc-field"><label htmlFor="rv-date">Kabul Tarihi</label><input id="rv-date" type="date" className="ic-in" value={receiptDate} max={today()} onChange={(e) => setReceiptDate(e.target.value)} /></div>
              <div className="rc-field"><label htmlFor="rv-wb">İrsaliye No</label><input id="rv-wb" className="ic-in" value={waybillNo} maxLength={50} placeholder="İsteğe bağlı" onChange={(e) => setWaybillNo(e.target.value)} /></div>
              <div className="rc-field"><label htmlFor="rv-note">Not</label><input id="rv-note" className="ic-in" value={note} maxLength={300} placeholder="İsteğe bağlı" onChange={(e) => setNote(e.target.value)} /></div>
            </div>

            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem" }}>
              <button type="button" className="rc-btn sm" onClick={fillAll}>Kalanın tamamı</button>
              <button type="button" className="rc-btn sm" onClick={clearAll}>Temizle</button>
            </div>

            <div className="rc-table-wrap">
              <table className="pu-table">
                <thead>
                  <tr>
                    <th className="c-no">#</th><th className="c-stock">Stok</th>
                    <th className="num">Sipariş</th><th className="num">Alınan</th><th className="num">Kalan</th>
                    <th className="num c-recv">Teslim Alınan</th>
                  </tr>
                </thead>
                <tbody>
                  {open.map((l, i) => (
                    <tr key={l.purchaseOrderLineId}>
                      <td className="c-no">{i + 1}</td>
                      <td className="c-stock" style={{ paddingTop: "0.75rem" }}>
                        <b>{l.stockName}</b>{l.stockCode ? <span className="pu-stock-hint"> · {l.stockCode}</span> : null}
                        {l.description && <div className="pu-stock-hint">{l.description}</div>}
                      </td>
                      <td className="num" style={{ paddingTop: "0.75rem" }}>{fmtQty(l.quantity)} {l.unit}</td>
                      <td className="num" style={{ paddingTop: "0.75rem" }}>{fmtQty(l.receivedQuantity)}</td>
                      <td className="num" style={{ paddingTop: "0.75rem" }}><b>{fmtQty(l.remaining)}</b></td>
                      <td className="num c-recv">
                        <input className="ic-in num" inputMode="decimal" value={qty[l.purchaseOrderLineId] ?? ""}
                          onChange={(e) => { setQty({ ...qty, [l.purchaseOrderLineId]: e.target.value }); setError(""); }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="ic-total-hint" style={{ marginTop: "0.75rem" }}>
              {hasModule("stock")
                ? "Teslim alınan miktarlar stoğa GİRİŞ olarak işlenir; stoğun son alış fiyatı ve tedarikçisi güncellenir."
                : "Stok modülü lisansta olmadığı için yalnız teslim miktarı izlenir, stok bakiyesi değişmez."}
            </div>
          </div>
        </div>

        <footer className="ic-actions">
          <span className="ic-actions-summary">{totalLines} satır teslim alınacak</span>
          <div className="ic-actions-btns">
            <button className="ic-btn" onClick={onClose} disabled={saving}>Vazgeç</button>
            <button className="ic-btn primary" onClick={save} disabled={saving || totalLines === 0}>
              {saving ? (<><span className="ic-spin" aria-hidden="true" />Kaydediliyor…</>) : "Mal Kabulü Kaydet"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ReceiveModal;
